// === WORKFLOW SUMMARY — offer-chain orchestration ===
// Extracted verbatim from WorkflowSummaryScreen: the four chain states with their
// sessionStorage-persisting wrappers, the price-relevant product-data hash, the
// session-ID safety clear, and the registerQuoteWithBackoffice effect
// (relation → calculate → package discount → quote). The effect dependency array
// is kept identical ([productDataHash]) to preserve the original timing.
// sessionStorage access goes through summary/sessionState.ts.

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  buildContractdocument,
  createRelation,
  buildCalculationDocument,
  calculateRelation,
  calculatePackageDiscount,
  generateQuote,
} from '@sluitstraat/core';
import { getProductOrder } from '../../../workflowConfig';
import { stableStringify } from '../../../utils/stableStringify';
import { cleanAvpRelationDocument, enrichCalculationDocumentForSaa } from './enrich';
import { sortOngevallenContracts } from '../ongevallen/calc';
import { sortPersonenautoContracts } from '../personenauto/calc';
import {
  aggregatePpTotalsForPackage,
  buildFallbackVpData,
  cleanPackageResponseProducts,
  extractCalculationId,
  dropDisabledInboedelGlass,
  extractQuoteId,
  extractVpData,
  extractVpIdnr,
  reseedQuoteResponsePremiums,
} from './calc';
import { inboedelGlassAllowed } from '../../../utils/glassPremium';
import {
  SUMMARY_SESSION_KEYS,
  clearAllChainState,
  clearCalculationArtifacts,
  clearRelationAndCalculationArtifacts,
  getCalculationId,
  getChainCompletedHash,
  getQuoteId,
  getRelationDataHash,
  getVpIdnr,
  readEnrichedDocument,
  readFlag,
  readVpData,
  setCalculationId,
  setChainCompletedHash,
  setQuoteId,
  setRelationDataHash,
  setVpIdnr,
  writeEnrichedDocument,
  writeFlag,
  writeVpData,
} from './sessionState';

interface OfferChainArgs {
  data: any;
  selectedProducts: any;
  config: any;
  t: (key: string) => string;
  ppEkst: number;
  baseUrl: string;
}

export function useOfferChain({ data, selectedProducts, config, t, ppEkst, baseUrl }: OfferChainArgs) {
  // Initialize states from sessionStorage to prevent flash on page reload
  const [relationCreated, setRelationCreated] = useState(() => !!getVpIdnr());
  const [premiumCalculated, setPremiumCalculatedState] = useState(() => {
    // Check direct flag first, then fallback to calculation_id
    return readFlag(SUMMARY_SESSION_KEYS.premiumCalculated) || !!getCalculationId();
  });

  // Wrapper to persist premiumCalculated to sessionStorage
  const setPremiumCalculated = (value: boolean) => {
    setPremiumCalculatedState(value);
    writeFlag(SUMMARY_SESSION_KEYS.premiumCalculated, value);
  };
  const [packageDiscountApplied, setPackageDiscountAppliedState] = useState(() => {
    // First check direct flag in sessionStorage
    if (readFlag(SUMMARY_SESSION_KEYS.packageDiscountApplied)) {
      return true;
    }
    // Fallback: check if enriched document has discount data
    const doc = readEnrichedDocument();
    const pakket = doc?.Pakket?.[0]?.Mantel?.PK;
    // Check if discount data exists
    return !!(pakket?.PK_PRCPKKT || pakket?.PK_TPAKKRT || pakket?.PK_TTOT);
  });

  // Wrapper to persist packageDiscountApplied to sessionStorage
  const setPackageDiscountApplied = (value: boolean) => {
    setPackageDiscountAppliedState(value);
    writeFlag(SUMMARY_SESSION_KEYS.packageDiscountApplied, value);
  };
  const [quoteGenerated, setQuoteGeneratedState] = useState(() => {
    return readFlag(SUMMARY_SESSION_KEYS.quoteGenerated) || !!getQuoteId();
  });

  // Wrapper to persist quoteGenerated to sessionStorage
  const setQuoteGenerated = (value: boolean) => {
    setQuoteGeneratedState(value);
    writeFlag(SUMMARY_SESSION_KEYS.quoteGenerated, value);
  };
  const [relationError, setRelationError] = useState<string | null>(null);
  const relationErrorRef = useRef<string | null>(null);
  const [isLoadingRelation, setIsLoadingRelation] = useState(false);
  const isProcessingRef = useRef(false); // Prevent duplicate calls while processing
  const pendingRerunRef = useRef(false); // A hash changed while a chain run was in flight
  const latestRunRef = useRef<() => void>(() => {}); // Newest effect closure (fresh hashes)
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Stable hash of price-relevant product data - only changes when actual premiums/coverages change
  const productDataHash = useMemo(() => {
    const hasAnyProductData =
      data['woonhuis']?.Contractdocument?.PP?.PP_TTOT ||
      data['inboedel']?.Contractdocument?.PP?.PP_TTOT ||
      data['aansprakelijkheid']?.Contractdocument?.PP?.DA?.DA_TTOT ||
      data['aansprakelijkheid']?.Contractdocument?.PP?.PP_TTOT ||
      data['personenauto']?.Contractdocument?.PP?.PP_TTOT ||
      data['personenauto']?.Contractdocument?.PP?.OB ||
      data['rechtsbijstand']?.Contractdocument?.PP?.WC ||
      data['ongevallen']?.Contractdocument?.PP?.PP_TTOT ||
      data['doorlopendereis']?.Contractdocument?.PP?.PP_TTOT;
    if (!hasAnyProductData) return '';
    return JSON.stringify({
      selectedProducts: selectedProducts,
      woonhuis: data['woonhuis']?.Contractdocument?.PP?.PP_TTOT,
      woonhuisDekkingen: data['woonhuis']?.Supportsection?.Dekkingen,
      inboedel: data['inboedel']?.Contractdocument?.PP?.PP_TTOT,
      inboedelDekkingen: data['inboedel']?.Supportsection?.Dekkingen || data['inboedel']?._uiState?.Dekkingen,
      aansprakelijkheid: data['aansprakelijkheid']?.Contractdocument?.PP?.DA?.DA_TTOT || data['aansprakelijkheid']?.Contractdocument?.PP?.PP_TTOT,
      personenauto: data['personenauto']?.Contractdocument?.PP?.PP_TTOT || data['personenauto']?.Contractdocument?.PP?.OB?.OB_TTOT,
      rechtsbijstand: data['rechtsbijstand']?.Contractdocument?.PP?.PP_TTOT || data['rechtsbijstand']?.Contractdocument?.PP?.WC?.WC_TTOT,
      ongevallen: data['ongevallen']?.Contractdocument?.PP?.PP_TTOT || data['ongevallen']?.Contractdocument?.PP?.OD?.OD_NTP,
      doorlopendereis: data['doorlopendereis']?.Contractdocument?.PP?.PP_TTOT,
    });
  }, [data, selectedProducts]);

  // Relation-relevant data: policyholder identity + the VP address + gezinssamenstelling,
  // mirroring exactly what buildRelatiedocument (core clientService) puts on the relation VP —
  // clientInfo fields plus the first product BA that carries an address, plus the first
  // DA_GSS/OP_GSS/IP_GSS it finds (copied onto the relation as VP_GSSC). Gezinssamenstelling
  // lives on the product pages, not on client-information, but it IS relation data: changing it
  // changes the relation VP. A change here means the STORED RELATION is stale; a premium change
  // only invalidates the calculation artifacts on top of it.
  const relationDataHash = useMemo(() => {
    const clientInfo = data['client-information']?.clientInfo || {};
    let ba: any = null;
    for (const key of Object.keys(data || {})) {
      const candidate = data[key]?.Contractdocument?.PP?.BA;
      if (candidate && (candidate.BA_STRAAT || candidate.BA_PCODE)) {
        ba = candidate;
        break;
      }
    }
    let gss: string | undefined;
    for (const key of Object.keys(data || {})) {
      const pp = data[key]?.Contractdocument?.PP;
      const candidate = pp?.DA?.DA_GSS || pp?.OP?.OP_GSS || pp?.IP?.IP_GSS;
      if (candidate) {
        gss = candidate;
        break;
      }
    }
    const relevant = {
      initials: clientInfo.initials,
      lastName: clientInfo.lastName,
      affix: clientInfo.affix,
      gender: clientInfo.gender,
      salutationCode: clientInfo.salutationCode,
      titulature: clientInfo.titulature,
      email: clientInfo.email,
      phone: clientInfo.phone,
      dateOfBirth: clientInfo.dateOfBirth,
      VP_GSSC: gss,
      address: ba
        ? {
            BA_STRAAT: ba.BA_STRAAT,
            BA_HUISNR: ba.BA_HUISNR,
            BA_TOEVOEG: ba.BA_TOEVOEG,
            BA_PCODE: ba.BA_PCODE,
            BA_PLAATS: ba.BA_PLAATS,
          }
        : undefined,
    };
    // Nothing loaded yet -> empty hash, so the stale-relation check below never fires on it
    const hasAny = Object.values(relevant).some((v) => v !== undefined && v !== null && v !== '');
    return hasAny ? stableStringify(relevant) : '';
  }, [data]);

  useEffect(() => {
    const hasContextData = Object.values(selectedProducts || {}).some(Boolean)
      && Object.keys(data || {}).length > 0;
    const hasStoredIds = getVpIdnr() || getCalculationId() || getQuoteId();

    if (hasStoredIds && !hasContextData) {
      clearAllChainState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Register quote with backoffice on mount:
  // creates the relation, calculates the premium, applies the package discount, and generates the quote.
  useEffect(() => {
    const registerQuoteWithBackoffice = async () => {
      // Prevent duplicate async calls while chain is in progress (also guards the
      // stale-relation clear below from yanking state away under a running chain).
      // Don't just drop the run: this effect only fires on hash change, so a change
      // arriving mid-run would otherwise never be reprocessed — the new hash never
      // stored, a stale relation never cleaned. Remember it; the finally block of the
      // in-flight run replays the latest closure.
      if (isProcessingRef.current) {
        pendingRerunRef.current = true;
        return;
      }

      // STALE RELATION: the policyholder (client info / VP address) changed after a relation
      // was created. Premium-artifact clearing keeps vp_idnr, which would re-quote against the
      // OLD relation — instead drop the relation ids plus all artifacts so the chain below
      // calls the relation endpoint again and recalculates everything against the new data.
      // Must run BEFORE the completed-hash skip: with unchanged premiums that gate would
      // otherwise short-circuit the whole chain.
      const storedRelationHash = getRelationDataHash();
      if (relationDataHash && storedRelationHash && storedRelationHash !== relationDataHash && getVpIdnr()) {
        clearRelationAndCalculationArtifacts();
        setRelationCreated(false);
        setPremiumCalculated(false);
        setPackageDiscountApplied(false);
        setQuoteGenerated(false);
      }
      if (relationDataHash && relationDataHash !== storedRelationHash) {
        setRelationDataHash(relationDataHash);
      }

      // GUARD: If chain completed with this exact product data, skip entirely
      // Uses a single completion hash instead of multiple flags to avoid race conditions
      const completedHash = getChainCompletedHash();
      if (completedHash && completedHash === productDataHash && productDataHash) {
        // Restore React states for button enabled state
        if (!relationCreated) setRelationCreated(true);
        if (!premiumCalculated) setPremiumCalculated(true);
        if (!packageDiscountApplied) setPackageDiscountApplied(true);
        if (!quoteGenerated) setQuoteGenerated(true);
        return;
      }

      // Skip if no product data loaded yet
      if (!productDataHash) return;

      // If product data changed since last completion, clear old calculation data
      // Preserve vp_idnr so the same relation is reused
      if (completedHash && completedHash !== productDataHash) {
        clearCalculationArtifacts();
      }

      // Read current step completion state
      const storedVpIdnr = getVpIdnr();
      const storedCalculationIdForProcessing = getCalculationId();
      const storedQuoteIdForProcessing = getQuoteId();

      // Mark as processing and show loading state
      isProcessingRef.current = true;
      setIsLoadingRelation(true);

      try {
        let vpIdnr = storedVpIdnr;
        let vpData = null;

        // Only create relation if we don't have a VP_IDNR yet
        if (!vpIdnr) {
          const relatieDocumentRaw = await buildContractdocument(data, selectedProducts);
          const relatieDocument = cleanAvpRelationDocument(relatieDocumentRaw);
          const relationResponse = await createRelation(relatieDocument);

          vpIdnr = extractVpIdnr(relationResponse);
          vpData = extractVpData(relationResponse);

          // Log the response for debugging if ID not found
          if (!vpIdnr) {
            console.error('[WorkflowSummary] VP_IDNR not found. Full response:', JSON.stringify(relationResponse, null, 2));
            throw new Error('VP_IDNR not found in relation response');
          }

          // Ensure VP_IDNR is always set in vpData (for 201 responses that return RelationId instead)
          if (vpData && !vpData.VP_IDNR) {
            vpData.VP_IDNR = vpIdnr;
          }

          setVpIdnr(vpIdnr);
          if (vpData) writeVpData(vpData);
          setRelationCreated(true);
        } else {
          vpData = readVpData();
          setRelationCreated(true);
        }

        // Build calculation document using clientService
        // Use canonical product order from workflowConfig (Personenauto → Woonhuis → Inboedel → AVP → Rechtsbijstand)
        const selectedProductKeys = getProductOrder().filter(key => selectedProducts[key]);
        const glassAllowedForInboedel = inboedelGlassAllowed(config, selectedProducts);

        // Ensure we have vpData for later steps
        if (!vpData) {
          // Build a minimal VP data structure from workflow data
          vpData = buildFallbackVpData(data['client-information']?.clientInfo || {}, vpIdnr);
        }

        let calculationDocument: any = null;

        // Step 1: Run calculation FIRST (with 3 products)
        // Only check for calculation ID - if missing, we need to calculate regardless of state flag
        // State can get out of sync with sessionStorage, so we rely on the ID as source of truth
        if (selectedProductKeys.length > 0 && !storedCalculationIdForProcessing) {

          // IMPORTANT: Clear package discount flag when starting fresh calculation
          // This ensures Step 2 (package discount) ALWAYS runs after a fresh calculation
          writeFlag(SUMMARY_SESSION_KEYS.packageDiscountApplied, false);

          // Build the calculation document
          calculationDocument = enrichCalculationDocumentForSaa(await buildCalculationDocument(data, vpData, selectedProductKeys), data, t, ppEkst, { baseUrl, inboedelGlassAllowed: glassAllowedForInboedel });

          const calculationResponse = await calculateRelation(vpIdnr, sortPersonenautoContracts(sortOngevallenContracts(calculationDocument)));

          // Store calculation ID (using calculationRecordId from response)
          const calculationId = extractCalculationId(calculationResponse);

          if (calculationId) {
            setCalculationId(calculationId);
            writeFlag(SUMMARY_SESSION_KEYS.premiumCalculated, true);
            setPremiumCalculated(true);
          } else {
            throw new Error('Calculation ID not found in response');
          }

          // Update calculation document with response data if available
          if (calculationResponse?.Relatiedocument) {
            // Use the complete Relatiedocument from calculation response
            calculationDocument = calculationResponse.Relatiedocument;
          } else if (calculationResponse?.Contract) {
            // Fallback: old Contract structure
            calculationResponse.Contract.forEach((contract: any, index: number) => {
              if (calculationDocument.Contract && calculationDocument.Contract[index]) {
                calculationDocument.Contract[index] = {
                  ...calculationDocument.Contract[index],
                  ...contract,
                  PP: {
                    ...calculationDocument.Contract[index].PP,
                    ...contract.PP
                  },
                  AL: {
                    ...calculationDocument.Contract[index].AL,
                    ...contract.AL
                  }
                };
              }
            });
          }

          // Store enriched document with calculation data
          writeEnrichedDocument(calculationDocument);
        } else if (storedCalculationIdForProcessing && !calculationDocument) {
          // Load enriched calculation document if we don't have it yet
          // Raw read + bare JSON.parse kept on purpose: a corrupt stored document must throw
          // into the outer catch (surface relationError), not silently fall back to a rebuild
          // the way readEnrichedDocument()'s swallow-and-null would.
          const storedEnrichedDoc = sessionStorage.getItem(SUMMARY_SESSION_KEYS.enrichedDocument);
          if (storedEnrichedDoc) {
            calculationDocument = JSON.parse(storedEnrichedDoc);
          } else {
            // Fallback: rebuild if not in storage
            calculationDocument = enrichCalculationDocumentForSaa(await buildCalculationDocument(data, vpData, selectedProductKeys), data, t, ppEkst, { baseUrl, inboedelGlassAllowed: glassAllowedForInboedel });
          }
          setPremiumCalculated(true);
        }

        // Step 2: Apply package discount AFTER calculation
        // Check sessionStorage directly - state can be out of sync if previous run was interrupted
        const storedPackageDiscountFlag = readFlag(SUMMARY_SESSION_KEYS.packageDiscountApplied);
        if (selectedProductKeys.length > 0 && calculationDocument && !storedPackageDiscountFlag) {

          // Preserve the original Relatiemantel with VP data before calling package endpoint
          const originalRelatiemantel = calculationDocument.Relatiemantel;

          // Fix PP-level premium totals before the package call — sum-of-sections + hidden
          // discount guards + PP_NTPVPK strip (moved verbatim to summary/calc.ts).
          aggregatePpTotalsForPackage(calculationDocument, { config, rbDekkingen: data['rechtsbijstand']?.Supportsection?.Dekkingen });

          // Call package endpoint with calculated data
          const packageResponse = await calculatePackageDiscount(vpIdnr, sortPersonenautoContracts(sortOngevallenContracts(calculationDocument)));

          // Use the entire Relatiedocument from package response
          // Package response structure: { Relatiedocument: { Relatiemantel, Contract: [], Pakket: [...] } }
          if (packageResponse?.Relatiedocument) {

            // Replace the entire calculation document with the package response Relatiedocument
            calculationDocument = packageResponse.Relatiedocument;

            // IMPORTANT: Restore the original Relatiemantel with VP data
            // The package endpoint may return an empty VP, but we need the full VP for subsequent calls
            if (originalRelatiemantel) {
              calculationDocument.Relatiemantel = originalRelatiemantel;
            }

            const pakketArray = calculationDocument.Pakket;
            if (pakketArray && Array.isArray(pakketArray) && pakketArray.length > 0) {
              const packageMantel = pakketArray[0]?.Mantel;

              if (packageMantel?.PK) {
                // Set PK_RDNMUT (always "1" - Reden aanvraag/mutatie code)
                if (!packageMantel.PK.PK_RDNMUT) {
                  packageMantel.PK.PK_RDNMUT = '1';
                }
              }
            }
          }

          // Clean unselected coverages, re-inject glass, recompute PP/PK totals, apply PK_TINV
          // and poliskosten (moved verbatim to summary/calc.ts).
          cleanPackageResponseProducts(calculationDocument, { data, config, selectedProducts, ppEkst, inboedelGlassAllowed: glassAllowedForInboedel });

          // Store enriched document with package data (now cleaned)
          writeEnrichedDocument(calculationDocument);

          setPackageDiscountApplied(true);
        } else if (storedPackageDiscountFlag && !calculationDocument) {
          // Package already applied, load the enriched document from sessionStorage
          // Raw read + bare JSON.parse kept on purpose — see the Step 1 load above.
          const storedEnrichedDoc = sessionStorage.getItem(SUMMARY_SESSION_KEYS.enrichedDocument);
          if (storedEnrichedDoc) {
            calculationDocument = JSON.parse(storedEnrichedDoc);

            // Safety check: Ensure VP data is present in loaded document
            if (!calculationDocument.Relatiemantel?.VP || Object.keys(calculationDocument.Relatiemantel.VP).length === 0) {
              if (!calculationDocument.Relatiemantel) {
                calculationDocument.Relatiemantel = {};
              }
              calculationDocument.Relatiemantel.VP = vpData;
            }
          } else {
            // Fallback: rebuild if not in storage (shouldn't happen)
            calculationDocument = enrichCalculationDocumentForSaa(await buildCalculationDocument(data, vpData, selectedProductKeys), data, t, ppEkst, { baseUrl, inboedelGlassAllowed: glassAllowedForInboedel });
          }
        }

        // Step 3: Generate quote with calculation and package data included
        if (selectedProductKeys.length > 0 && !storedQuoteIdForProcessing && calculationDocument) {
          // Safety check: Ensure VP data is present before generating quote
          if (!calculationDocument.Relatiemantel?.VP || Object.keys(calculationDocument.Relatiemantel.VP).length === 0) {
            if (!calculationDocument.Relatiemantel) {
              calculationDocument.Relatiemantel = {};
            }
            calculationDocument.Relatiemantel.VP = vpData;
          }

          // Last gate before the wire: this document may have come straight from sessionStorage
          // without passing enrich.ts or cleanPackageResponseProducts (see the helper's note).
          // /quote is the call an unratable GP takes down entirely, so it is gated here too.
          dropDisabledInboedelGlass(calculationDocument, glassAllowedForInboedel);

          // Get calculationId from sessionStorage to include in quote request
          const calculationId = getCalculationId() || undefined;

          const quoteResponse = await generateQuote(vpIdnr, sortPersonenautoContracts(sortOngevallenContracts(calculationDocument)), calculationId);

          const quoteId = extractQuoteId(quoteResponse);

          if (quoteId) {
            setQuoteId(quoteId);
            setQuoteGenerated(true);

            // Merge quote response data into calculation document
            if (quoteResponse?.Relatiedocument) {
              // Use the complete Relatiedocument from quote response
              calculationDocument = quoteResponse.Relatiedocument;

              reseedQuoteResponsePremiums(calculationDocument, { data, inboedelGlassAllowed: glassAllowedForInboedel });
            } else if (quoteResponse?.Contract) {
              // Fallback: old Contract structure
              quoteResponse.Contract.forEach((contract: any, index: number) => {
                if (calculationDocument.Contract && calculationDocument.Contract[index]) {
                  calculationDocument.Contract[index] = {
                    ...calculationDocument.Contract[index],
                    ...contract,
                    PP: {
                      ...calculationDocument.Contract[index].PP,
                      ...contract.PP
                    },
                    AL: {
                      ...calculationDocument.Contract[index].AL,
                      ...contract.AL
                    }
                  };
                }
              });
            }

            // Store final enriched document with quote data
            writeEnrichedDocument(calculationDocument);
          } else {
            throw new Error('Quote ID not found in response');
          }
        } else if (storedQuoteIdForProcessing) {
          setQuoteGenerated(true);
        }

        // Mark chain as fully completed with this product data hash
        // On remount, this hash is compared to skip redundant API calls
        setChainCompletedHash(productDataHash);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        relationErrorRef.current = errorMessage;
        setRelationError(errorMessage);
      } finally {
        // Reset processing flag and loading state
        isProcessingRef.current = false;
        setIsLoadingRelation(false);
        if (pendingRerunRef.current) {
          pendingRerunRef.current = false;
          if (!relationErrorRef.current && mountedRef.current) {
            latestRunRef.current();
          }
        }
      }
    };

    latestRunRef.current = registerQuoteWithBackoffice;
    if (relationErrorRef.current) {
      relationErrorRef.current = null;
      setRelationError(null);
    }
    registerQuoteWithBackoffice();
    // Re-run when the price-relevant product data OR the relation-relevant client data changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productDataHash, relationDataHash]);

  return { relationCreated, premiumCalculated, packageDiscountApplied, quoteGenerated, relationError, isLoadingRelation };
}
